import React, { useMemo, useState } from 'react';
import LoyaltyDesigner from '../loyalty-v2/LoyaltyDesigner';
import LoyaltyCard from '../loyalty-v2/LoyaltyCard';
import { THEMES, STAMP_ICONS, defaultDesign } from '../loyalty-v2/data';

/**
 * A loyalty card you can actually change, inside the conversation.
 *
 * This used to be numbers plus a button into the full designer. The chat was
 * gentle and guided and then the tap dropped you into a canvas with layers,
 * shapes and text — a seam you could feel.
 *
 * The fix is not to hide the designer. It is to separate whose medium it is.
 * The builder is BAILEY's: it is why a proposal can be a real stamp card
 * rather than a description of one, and it stays the one source of truth for
 * the artifact. But it should not be the salon's first interface.
 *
 * So the first move is bounded. A stepper, a reward, sixteen art-directed
 * palettes and a handful of stamps — every combination is one somebody
 * already decided looks good, so there is no way to make a bad card and no
 * reason not to play. The full designer drops to a quiet link for the few who
 * want the canvas, and it opens pre-filled with whatever was changed here.
 *
 * The rule this is built on: in the assistant, a choice is never a blank
 * canvas. Bailey's job is to have made the hard decisions already and offer a
 * small set where every option is a good one.
 */

const money = (n) => `£${Number(n || 0).toLocaleString('en-GB')}`;

// Bounds. Below three a card is a discount, above ten nobody finishes it.
const MIN_VISITS = 3;
const MAX_VISITS = 10;

// A handful of stamps rather than the full set — the designer has the rest.
const QUICK_ICONS = ['scissors', 'sparkle', 'heart', 'star', 'flower', 'crown'];

const IconGlyph = ({ icon }) => (icon.emoji
    ? <span aria-hidden>{icon.emoji}</span>
    : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden>{icon.svg}</svg>
    ));

export default function BuilderCard({ card, salonName, existingScheme, onSave }) {
    const proposed = card && card.payload;
    const basis = (card && card.basis) || {};
    const note = (card && card.estimate && card.estimate.note) || '';

    const [scheme, setScheme] = useState(() => ({ ...proposed }));
    const [design, setDesign] = useState(() => ({ ...defaultDesign() }));
    const [designerOpen, setDesignerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // What it costs, recomputed as they adjust. Same formula the server used
    // to size it, so the number never disagrees with the one Bailey quoted.
    const costPct = useMemo(() => {
        const avg = Number(basis.averageTicket) || 0;
        const visits = Number(scheme.stampsRequired) || 0;
        const reward = Number(scheme.rewardCap) || 0;
        if (!avg || !visits) return null;
        return Math.round((reward / (avg * visits)) * 10000) / 100;
    }, [basis.averageTicket, scheme.stampsRequired, scheme.rewardCap]);

    if (!proposed) return null;

    // One scheme per salon, and saving replaces it. Offering a cheerful
    // "start collecting" here would silently throw away a card clients are
    // part-way through — so when one exists, this stops being a create.
    if (existingScheme) {
        return (
            <div className="agt-card">
                <h4 className="agt-card-title">You already have a loyalty card</h4>
                <p className="agt-card-body">
                    {existingScheme.name ? `“${existingScheme.name}” is ` : 'It’s '}
                    {existingScheme.status === 'live' ? 'running now' : 'set up but paused'}.
                    Changing it here would replace it, and clients part-way through would
                    lose their stamps. Best done in the designer, where you can see what
                    changes.
                </p>
                <div className="agt-actions">
                    <button className="agt-btn" onClick={() => setDesignerOpen(true)}>
                        Open the designer
                    </button>
                </div>
                {designerOpen && (
                    <LoyaltyDesigner
                        scheme={existingScheme}
                        salonName={salonName}
                        onClose={() => setDesignerOpen(false)}
                        onSave={async (next) => { await onSave(next); setDesignerOpen(false); }}
                    />
                )}
            </div>
        );
    }

    const set = (patch) => setScheme((s) => ({ ...s, ...patch }));
    const visits = Math.max(MIN_VISITS, Math.min(MAX_VISITS, Number(scheme.stampsRequired) || 6));
    const reward = Number(scheme.rewardCap) || 0;

    const setVisits = (n) => {
        const v = Math.max(MIN_VISITS, Math.min(MAX_VISITS, n));
        set({ stampsRequired: v });
    };
    const setReward = (n) => {
        const r = Math.max(5, n);
        set({ rewardCap: r, rewardLabel: `£${r} off your next visit` });
    };

    const save = async (status) => {
        setSaving(true);
        setError(null);
        try {
            await onSave({ ...scheme, stampsRequired: visits, status, design });
        } catch (e) {
            setError('That didn’t save. Nothing has changed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="agt-card agt-studio">
            <h4 className="agt-card-title">A loyalty card for {salonName || 'your salon'}</h4>

            {/* The live thing, first and biggest. Not editable here — the
                controls below are the way to change it, and a draggable
                canvas is what the designer is for. */}
            <div className="agt-studio-stage">
                <LoyaltyCard
                    design={design}
                    scheme={{ ...scheme, stampsRequired: visits }}
                    salonName={salonName || 'Your Salon'}
                    width={320}
                />
            </div>

            <div className="agt-studio-controls">
                <div className="agt-studio-row">
                    <span className="agt-studio-label">Reward after</span>
                    <div className="agt-stepper">
                        <button type="button" aria-label="Fewer visits"
                            disabled={visits <= MIN_VISITS} onClick={() => setVisits(visits - 1)}>−</button>
                        <strong aria-live="polite">{visits} visits</strong>
                        <button type="button" aria-label="More visits"
                            disabled={visits >= MAX_VISITS} onClick={() => setVisits(visits + 1)}>+</button>
                    </div>
                </div>

                <div className="agt-studio-row">
                    <span className="agt-studio-label">They get</span>
                    <div className="agt-stepper">
                        <button type="button" aria-label="Smaller reward"
                            disabled={reward <= 5} onClick={() => setReward(reward - 5)}>−</button>
                        <strong aria-live="polite">{money(reward)} off</strong>
                        <button type="button" aria-label="Bigger reward"
                            onClick={() => setReward(reward + 5)}>+</button>
                    </div>
                </div>

                <div className="agt-studio-row is-stacked">
                    <span className="agt-studio-label">Look</span>
                    <div className="agt-swatches" role="group" aria-label="Card look">
                        {THEMES.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className="agt-swatch"
                                aria-label={t.name}
                                aria-pressed={design.themeId === t.id}
                                style={{ background: t.bg, color: t.accent }}
                                onClick={() => setDesign((d) => ({ ...d, themeId: t.id }))}
                            />
                        ))}
                    </div>
                </div>

                <div className="agt-studio-row is-stacked">
                    <span className="agt-studio-label">Stamp</span>
                    <div className="agt-stamps" role="group" aria-label="Stamp">
                        {QUICK_ICONS
                            .map((id) => STAMP_ICONS.find((i) => i.id === id))
                            .filter(Boolean)
                            .map((icon) => (
                                <button
                                    key={icon.id}
                                    type="button"
                                    className="agt-stamp"
                                    aria-label={icon.label}
                                    aria-pressed={design.icon === icon.id}
                                    onClick={() => setDesign((d) => ({ ...d, icon: icon.id }))}
                                >
                                    <IconGlyph icon={icon} />
                                </button>
                            ))}
                    </div>
                </div>
            </div>

            {/* The honest part, kept: what it was sized on, and what it costs
                as they move it. A number with no workings behind it is just a
                number, and this one decides what they give away. */}
            <p className="agt-card-body agt-studio-basis">
                Sized on {basis.completedBookings} completed bookings over{' '}
                {basis.lookbackDays} days. Average spend {money(basis.averageTicket)}
                {basis.medianReturnGapDays != null
                    && `, and people come back about every ${basis.medianReturnGapDays} days`}.
                {costPct != null && ` At this setting it costs about ${costPct}% of what those visits bring in.`}
                {note && ` ${note}`}
            </p>

            {error && <p className="agt-card-body" style={{ color: '#B42318' }}>{error}</p>}

            <div className="agt-actions">
                <button className="agt-btn" disabled={saving} onClick={() => save('paused')}>
                    Save for later
                </button>
                <button className="agt-btn is-primary" disabled={saving} onClick={() => save('live')}>
                    {saving ? 'Saving…' : 'Start collecting'}
                </button>
            </div>
            <p className="agt-card-body agt-studio-foot">
                {/* Said plainly, because one of these buttons starts a real
                    thing that clients will see on their next visit. */}
                Starting it means clients begin earning on their next visit.{' '}
                <button type="button" className="agt-linkbtn" onClick={() => setDesignerOpen(true)}>
                    Open the full designer
                </button>{' '}
                if you want to change the wording, rules or artwork.
            </p>

            {designerOpen && (
                <LoyaltyDesigner
                    // Pre-filled with what they changed here, so opening the
                    // canvas continues the work rather than restarting it.
                    scheme={{ ...scheme, stampsRequired: visits, design }}
                    salonName={salonName}
                    isCreate
                    onClose={() => setDesignerOpen(false)}
                    onSave={async (next) => { await onSave(next); setDesignerOpen(false); }}
                />
            )}
        </div>
    );
}
